import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, DatePicker, Input, InputNumber, Modal, Select, Space, Tooltip, Typography, message } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { Dayjs } from 'dayjs';

import StandardTable from '../../components/common/StandardTable';

const { RangePicker } = DatePicker;
const { Text } = Typography;

type DateRange = [Dayjs | null, Dayjs | null] | null;

type LocalFileRow = {
  id: string;
  file_name: string;
  path: string;      // absolute-like path for display / clipboard
  relPath: string;   // relative path from selected root for FS API access
  type: string;
  size: number | null;
  date_created: string | null;
  date_modified: string | null;
};

const DccLocalFiles: React.FC = () => {
  const navigate = useNavigate();

  const [directoryHandle, setDirectoryHandle] = useState<any>(null);
  const [rows, setRows] = useState<LocalFileRow[]>([]);

  const [fileNameFilter, setFileNameFilter] = useState('');
  const [debouncedFileName, setDebouncedFileName] = useState('');
  const [pathFilter, setPathFilter] = useState('');
  const [debouncedPath, setDebouncedPath] = useState('');
  const [typeFilter, setTypeFilter] = useState<string[]>([]);

  const [dateModifiedRange, setDateModifiedRange] = useState<DateRange>(null);

  const [tablePageSize, setTablePageSize] = useState<number>(50);
  const [tableCurrentPage, setTableCurrentPage] = useState<number>(1);

  const [batchOffset, setBatchOffset] = useState<number>(0);
  const [batchSize, setBatchSize] = useState<number>(500);

  const [dirQueue, setDirQueue] = useState<Array<{ handle: any; basePath: string }>>([]);
  const [hasMoreFiles, setHasMoreFiles] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);

  const errorModalOpenRef = useRef<boolean>(false);

  const [preview, setPreview] = useState<{ url: string | null; x: number; y: number }>({ url: null, x: 0, y: 0 });
  const [absoluteRootPath, setAbsoluteRootPath] = useState<string>('');
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const [thumbSize, setThumbSize] = useState<number>(200);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [destinationHandle, setDestinationHandle] = useState<any>(null);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState<boolean>(false);
  const [isCopying, setIsCopying] = useState<boolean>(false);
  const [currentCopyName, setCurrentCopyName] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedFileName(fileNameFilter), 300);
    return () => clearTimeout(t);
  }, [fileNameFilter]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedPath(pathFilter), 300);
    return () => clearTimeout(t);
  }, [pathFilter]);

  const isFileSystemApiAvailable = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return 'showDirectoryPicker' in window;
  }, []);

  const normalize = useCallback((v: any) => (v == null ? '' : String(v)).trim(), []);

  const formatErrorWithContext = useCallback(
    (err: any, ctx?: { op?: string; relPath?: string; basePath?: string }) => {
      const op = ctx?.op ? String(ctx.op) : '';
      const relPath = ctx?.relPath ? String(ctx.relPath) : '';
      const basePath = ctx?.basePath ? String(ctx.basePath) : '';

      const originalMessage = err?.message ? String(err.message) : String(err || '');
      const whereParts = [relPath ? `path=${relPath}` : '', basePath ? `dir=${basePath}` : ''].filter(Boolean);
      const where = whereParts.length ? ` (${whereParts.join(', ')})` : '';
      const prefix = op ? `${op}${where}: ` : where ? `${where}: ` : '';

      return {
        name: err?.name,
        message: `${prefix}${originalMessage}`.trim(),
        cause: err,
        context: { op, relPath, basePath },
      };
    },
    []
  );

  const formatErrorForDisplay = useCallback((err: any) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (err?.message) return String(err.message);
    try {
      return JSON.stringify(err);
    } catch {
      return String(err);
    }
  }, []);

  useEffect(() => {
    if (!preview.url) return undefined;
    const url = preview.url;
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [preview.url]);

  const selectLocalFolder = useCallback(async () => {
    if (!isFileSystemApiAvailable) {
      message.warning('File System Access API is not available in this browser.');
      return;
    }
    try {
      const handle = await (window as any).showDirectoryPicker();
      if (!handle) return;
      if (typeof handle.requestPermission === 'function') {
        try {
          await handle.requestPermission({ mode: 'read' });
        } catch {}
      }

      const input = window.prompt(
        'Enter the absolute path for this folder (for display and clipboard copy, e.g. D:/DCC_ARCHIVE):',
        ''
      );
      const normalizedRoot = (input || '').trim();

      setDirectoryHandle(handle);
      setAbsoluteRootPath(normalizedRoot);
      setRows([]);
      setDirQueue([{ handle, basePath: '' }]);
      setHasMoreFiles(true);
      setBatchOffset(0);
      setTableCurrentPage(1);
      message.success('Local folder selected. Listing files...');
    } catch (err: any) {
      if (err && (err.name === 'AbortError' || err.name === 'NotAllowedError')) return;
      message.error('Failed to select local folder.');
    }
  }, [isFileSystemApiAvailable]);

  const loadMoreFiles = useCallback(
    async (minToLoad: number) => {
      if (!directoryHandle) return;
      if (!dirQueue.length) {
        setHasMoreFiles(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      const ctx: { op?: string; relPath?: string; basePath?: string } = {}; 

      try {
        const items: LocalFileRow[] = [...rows];
        let idCounter = items.length + 1;
        const queue: Array<{ handle: any; basePath: string }> = [...dirQueue];
        let added = 0;

        while (queue.length > 0 && added < minToLoad) {
          const current = queue.shift();
          if (!current) break;
          const { handle: dir, basePath } = current;
          ctx.basePath = basePath;
          ctx.op = 'dir.entries';

          // eslint-disable-next-line no-restricted-syntax
          for await (const [nameFromIter, handle] of dir.entries()) {
            const entry: any = handle;
            const name = nameFromIter || entry?.name || '';
            const relPath = basePath ? `${basePath}/${name}` : name;
            ctx.relPath = relPath;

            if (!entry) continue;

            if (entry.kind === 'directory') {
              queue.push({ handle: entry, basePath: relPath });
              continue;
            }

            if (entry.kind !== 'file') continue;

            ctx.op = 'entry.getFile';
            const file = await entry.getFile();
            const displayName = file.name || name;
            const size = typeof file.size === 'number' && isFinite(file.size) ? file.size : null;
            const type = file.type || '';
            const lastMod = file.lastModified ? new Date(file.lastModified).toISOString() : null;

            const base = absoluteRootPath.trim();
            const fullPath = base
              ? `${base.replace(/[\\/]+$/, '')}\\${relPath.replace(/\//g, '\\')}`
              : relPath.replace(/\//g, '\\');

            items.push({
              id: String(idCounter++),
              file_name: displayName,
              path: fullPath,
              relPath,
              type,
              size,
              date_created: null,
              date_modified: lastMod,
            });

            added += 1;
            if (added >= minToLoad) break;
          }
        }

        setRows(items);
        setDirQueue(queue);
        setHasMoreFiles(queue.length > 0);
      } catch (err: any) {
        setError(formatErrorWithContext(err, ctx));
      } finally {
        setIsLoading(false);
      }
    },
    [absoluteRootPath, dirQueue, directoryHandle, formatErrorWithContext, rows]
  );

  const stopReadingFiles = useCallback(() => {
    setHasMoreFiles(false);
    setDirQueue([]);
    setIsLoading(false);
    setError(null);
  }, []);

  const continueReadingFiles = useCallback(async () => {
    setError(null);
    if (!directoryHandle) return;
    if (!dirQueue.length) return;
    const effectiveSize = batchSize > 0 ? batchSize : 500;
    await loadMoreFiles(effectiveSize);
  }, [batchSize, dirQueue.length, directoryHandle, loadMoreFiles]);

  useEffect(() => {
    if (!error) return;
    if (errorModalOpenRef.current) return;

    errorModalOpenRef.current = true;
    Modal.confirm({
      title: 'Error while reading local files',
      content: formatErrorForDisplay(error),
      okText: 'Continue (ignore error)',
      cancelText: 'Stop',
      onOk: async () => {
        try {
          await continueReadingFiles();
        } finally {
          errorModalOpenRef.current = false;
        }
      },
      onCancel: () => {
        stopReadingFiles();
        errorModalOpenRef.current = false;
      },
      centered: true,
    });
  }, [continueReadingFiles, error, formatErrorForDisplay, stopReadingFiles]);

  useEffect(() => {
    if (!directoryHandle) return;
    if (!dirQueue.length) return;
    if (rows.length > 0) return;

    const effectiveSize = batchSize > 0 ? batchSize : 500;
    loadMoreFiles(effectiveSize);
  }, [batchSize, dirQueue, directoryHandle, loadMoreFiles, rows.length]);

  const typeOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => {
      const t = normalize(r.type).toLowerCase();
      if (t) set.add(t);
    });
    return Array.from(set.values()).sort((a, b) => a.localeCompare(b));
  }, [normalize, rows]);

  const modifiedFrom = useMemo(() => {
    const start = dateModifiedRange?.[0];
    return start ? start.startOf('day').toISOString() : undefined;
  }, [dateModifiedRange]);

  const modifiedTo = useMemo(() => {
    const end = dateModifiedRange?.[1];
    return end ? end.endOf('day').toISOString() : undefined;
  }, [dateModifiedRange]);

  const filteredRows = useMemo(() => {
    const fnQ = normalize(debouncedFileName).toLowerCase();
    const pathQ = normalize(debouncedPath).toLowerCase();
    const typeSet = new Set((typeFilter || []).map(t => normalize(t).toLowerCase()).filter(Boolean));
    const mFrom = modifiedFrom ? Date.parse(modifiedFrom) : null;
    const mTo = modifiedTo ? Date.parse(modifiedTo) : null;

    return rows.filter(r => {
      if (fnQ && !normalize(r.file_name).toLowerCase().includes(fnQ)) return false;
      if (pathQ && !normalize(r.path).toLowerCase().includes(pathQ)) return false;
      if (typeSet.size > 0) {
        const t = normalize(r.type).toLowerCase();
        if (!typeSet.has(t)) return false;
      }
      if (mFrom != null || mTo != null) {
        const raw = r.date_modified;
        const ts = raw ? Date.parse(String(raw)) : NaN;
        if (!Number.isFinite(ts)) return false;
        if (mFrom != null && ts < mFrom) return false;
        if (mTo != null && ts > mTo) return false;
      }
      return true;
    });
  }, [debouncedFileName, debouncedPath, modifiedFrom, modifiedTo, normalize, rows, typeFilter]);

  const currentBatchRows = useMemo(() => {
    const effectiveSize = batchSize > 0 ? batchSize : 500;
    const start = batchOffset < 0 ? 0 : batchOffset;
    const end = start + effectiveSize;
    return filteredRows.slice(start, end);
  }, [batchOffset, batchSize, filteredRows]);

  const hasMoreBatches = useMemo(() => {
    const effectiveSize = batchSize > 0 ? batchSize : 500;
    return filteredRows.length > batchOffset + effectiveSize || hasMoreFiles;
  }, [batchOffset, batchSize, filteredRows.length, hasMoreFiles]);

  const handleTableChange = (pagination: any) => {
    const nextCurrent = pagination?.current;
    const nextSize = pagination?.pageSize;
    if (typeof nextCurrent === 'number' && isFinite(nextCurrent) && nextCurrent > 0) {
      setTableCurrentPage(nextCurrent);
    }
    if (typeof nextSize === 'number' && isFinite(nextSize) && nextSize > 0) {
      setTablePageSize(nextSize);
    }
  };

  const goPrevBatch = () => {
    const effectiveSize = batchSize > 0 ? batchSize : 500;
    setBatchOffset(prev => {
      const next = prev - effectiveSize;
      return next < 0 ? 0 : next;
    });
    setTableCurrentPage(1);
  };

  const goNextBatch = async () => {
    if (!hasMoreBatches) return;
    const effectiveSize = batchSize > 0 ? batchSize : 500;

    const nextOffset = batchOffset + effectiveSize;
    if (rows.length < nextOffset + 1 && hasMoreFiles) {
      await loadMoreFiles(effectiveSize);
    }

    setBatchOffset(prev => prev + effectiveSize);
    setTableCurrentPage(1);
  };

  const handleRefresh = () => {
    if (!directoryHandle) {
      message.info('Select a local folder first.');
      return;
    }

    setRows([]);
    setDirQueue([{ handle: directoryHandle, basePath: '' }]);
    setHasMoreFiles(true);
    setBatchOffset(0);
    setTableCurrentPage(1);
  };

  const formatDateDisplay = useCallback((v: any) => {
    if (v == null) return '';
    const s = String(v);
    if (s.includes('T') && s.length >= 16) {
      return s.slice(0, 16).replace('T', ' ');
    }
    return s;
  }, []);

  const columns = useMemo(
    () => [
      {
        title: 'File Name',
        dataIndex: 'file_name',
        key: 'file_name',
        width: 240,
        render: (text: string) => (
          <Tooltip placement="topLeft" title={text}>
            <span style={{ display: 'inline-block', maxWidth: '100%', whiteSpace: 'nowrap' }}>{text}</span>
          </Tooltip>
        ),
      },
      {
        title: 'Path',
        dataIndex: 'path',
        key: 'path',
        width: 420,
        render: (text: string) => (
          <Tooltip placement="topLeft" title={text}>
            <span style={{ display: 'inline-block', maxWidth: '100%', whiteSpace: 'nowrap' }}>{text}</span>
          </Tooltip>
        ),
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: 140,
        render: (text: string) => (
          <Tooltip placement="topLeft" title={text}>
            <span style={{ display: 'inline-block', maxWidth: '100%', whiteSpace: 'nowrap' }}>{text}</span>
          </Tooltip>
        ),
      },
      {
        title: 'Size',
        dataIndex: 'size',
        key: 'size',
        width: 120,
        render: (v: any) => (v == null ? '' : String(v)),
      },
      {
        title: 'Date Modified',
        dataIndex: 'date_modified',
        key: 'date_modified',
        width: 160,
        render: (v: any) => {
          if (v == null) return '';
          const full = String(v);
          const short = formatDateDisplay(v);
          return (
            <Tooltip placement="topLeft" title={full}>
              <span style={{ display: 'inline-block', maxWidth: '100%', whiteSpace: 'nowrap' }}>{short}</span>
            </Tooltip>
          );
        },
      },
    ],
    [formatDateDisplay]
  );

  const handleRowPreview = useCallback(
    async (event: React.MouseEvent, record: LocalFileRow) => {
      if (!directoryHandle) {
        message.info('Select a local folder first.');
        return;
      }
      try {
        const rel = record.relPath || record.file_name;
        const segments = rel.split(/[\\/]+/).filter(Boolean);
        let current: any = directoryHandle;
        for (let i = 0; i < segments.length; i += 1) {
          const seg = segments[i];
          const isLast = i === segments.length - 1;
          if (isLast) {
            current = await current.getFileHandle(seg, { create: false });
          } else {
            current = await current.getDirectoryHandle(seg, { create: false });
          }
        }

        const file = await current.getFile();
        const url = URL.createObjectURL(file);
        setPreview({ url, x: event.clientX + 15, y: event.clientY + 15 });
      } catch (err) {
        const pathToCopy = record?.path;
        if (pathToCopy && navigator?.clipboard?.writeText) {
          navigator.clipboard
            .writeText(String(pathToCopy))
            .then(() =>
              message.info(
                'Could not load preview. File path copied to clipboard so you can open it via Explorer.'
              )
            )
            .catch(() => message.warning('Could not load preview and failed to copy file path.'));
        } else {
          message.warning('Could not load preview for this file.');
        }
      }
    },
    [directoryHandle]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!currentBatchRows.length) return;
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      event.preventDefault();

      let currentIndex = currentBatchRows.findIndex(r => r.id === activeRowId);
      if (currentIndex === -1) {
        currentIndex = 0;
      }

      if (event.key === 'ArrowDown' && currentIndex < currentBatchRows.length - 1) {
        currentIndex += 1;
      } else if (event.key === 'ArrowUp' && currentIndex > 0) {
        currentIndex -= 1;
      }

      const nextRow = currentBatchRows[currentIndex];
      if (!nextRow) return;

      setActiveRowId(nextRow.id);

      const syntheticEvent = {
        clientX: window.innerWidth / 2,
        clientY: window.innerHeight / 2,
      } as React.MouseEvent;
      handleRowPreview(syntheticEvent, nextRow);
    },
    [activeRowId, currentBatchRows, handleRowPreview]
  );

  const handleRowOpenInNewTab = useCallback(
    async (record: LocalFileRow) => {
      if (!directoryHandle) {
        message.info('Select a local folder first.');
        return;
      }
      try {
        const rel = record.relPath || record.file_name;
        const segments = rel.split(/[\\/]+/).filter(Boolean);
        let current: any = directoryHandle;
        for (let i = 0; i < segments.length; i += 1) {
          const seg = segments[i];
          const isLast = i === segments.length - 1;
          if (isLast) {
            current = await current.getFileHandle(seg, { create: false });
          } else {
            current = await current.getDirectoryHandle(seg, { create: false });
          }
        }

        const file = await current.getFile();
        const url = URL.createObjectURL(file);
        const opened = window.open(url, '_blank');
        if (!opened) {
          message.warning('Browser blocked opening the file in a new tab.');
        }
      } catch (err) {
        const pathToCopy = record?.path;
        if (pathToCopy && navigator?.clipboard?.writeText) {
          navigator.clipboard
            .writeText(String(pathToCopy))
            .then(() =>
              message.info(
                'Could not open this file in a new tab. File path copied to clipboard so you can open it via Explorer.'
              )
            )
            .catch(() =>
              message.warning('Could not open this file in a new tab and failed to copy file path.')
            );
        } else {
          message.warning('Could not open this file in a new tab.');
        }
      }
    },
    [directoryHandle]
  );

  const resetFilters = () => {
    setFileNameFilter('');
    setPathFilter('');
    setTypeFilter([]);
    setDateModifiedRange(null);
    setBatchOffset(0);
    setTableCurrentPage(1);
  };

  const selectDestinationFolder = useCallback(async () => {
    if (!isFileSystemApiAvailable) {
      message.warning('File System Access API is not available in this browser.');
      return;
    }
    try {
      const handle = await (window as any).showDirectoryPicker();
      if (!handle) return;
      if (typeof handle.requestPermission === 'function') {
        try {
          await handle.requestPermission({ mode: 'readwrite' });
        } catch {}
      }
      setDestinationHandle(handle);
      message.success('Destination folder selected.');
    } catch (err: any) {
      if (err && (err.name === 'AbortError' || err.name === 'NotAllowedError')) return;
      message.error('Failed to select destination folder.');
    }
  }, [isFileSystemApiAvailable]);

  const copySelectedToDestination = useCallback(async () => {
    if (!directoryHandle) {
      message.info('Select a local folder first.');
      return;
    }
    if (!destinationHandle) {
      message.info('Select a destination folder first.');
      return;
    }
    const ids = selectedRowIds;
    if (!ids.length) {
      message.info('No files selected to copy.');
      return;
    }

    const rowsById = new Map<string, LocalFileRow>();
    rows.forEach(r => {
      rowsById.set(String(r.id), r);
    });

    setIsCopying(true);
    setCurrentCopyName(null);
    try {
      let successCount = 0;
      let failCount = 0;

      // Copy files one by one; preserve file names, flattening into destination root
      // (folder hierarchy is represented in relPath but not recreated under destination).
      const getUniqueDestFileHandle = async (baseName: string): Promise<any> => {
        const dotIndex = baseName.lastIndexOf('.');
        const hasExt = dotIndex > 0;
        const namePart = hasExt ? baseName.slice(0, dotIndex) : baseName;
        const extPart = hasExt ? baseName.slice(dotIndex) : '';

        let attempt = 0;
        // Try base name, then `name (1).ext`, `name (2).ext`, ...
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const candidate = attempt === 0 ? baseName : `${namePart} (${attempt})${extPart}`;
          try {
            // eslint-disable-next-line no-await-in-loop
            await destinationHandle.getFileHandle(candidate, { create: false });
          } catch {
            // File does not exist; create and return this handle.
            // eslint-disable-next-line no-await-in-loop
            return destinationHandle.getFileHandle(candidate, { create: true });
          }
          attempt += 1;
        }
      };

      for (const id of ids) {
        const row = rowsById.get(String(id));
        if (!row) {
          // eslint-disable-next-line no-continue
          continue;
        }
        try {
          const rel = row.relPath || row.file_name;
          const segments = rel.split(/[\\/]+/).filter(Boolean);
          if (!segments.length) {
            // eslint-disable-next-line no-continue
            continue;
          }

          let current: any = directoryHandle;
          for (let i = 0; i < segments.length - 1; i += 1) {
            const seg = segments[i];
            current = await current.getDirectoryHandle(seg, { create: false });
          }

          const leafName = segments[segments.length - 1];
          const srcFileHandle = await current.getFileHandle(leafName, { create: false });
          const srcFile = await srcFileHandle.getFile();
          setCurrentCopyName(srcFile.name);

          const destFileHandle = await getUniqueDestFileHandle(srcFile.name);
          const writable = await destFileHandle.createWritable();
          await writable.write(srcFile);
          await writable.close();

          successCount += 1;
        } catch {
          failCount += 1;
        }
      }

      if (successCount && !failCount) {
        message.success(`Copied ${successCount} file(s) to destination folder.`);
      } else if (successCount && failCount) {
        message.warning(`Copied ${successCount} file(s), but ${failCount} file(s) failed.`);
      } else if (!successCount && failCount) {
        message.error('Failed to copy selected files.');
      }
    } finally {
      setIsCopying(false);
      setCurrentCopyName(null);
    }
  }, [destinationHandle, directoryHandle, rows, selectedRowIds]);

  return (
    <div
      className="compact-table-container"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onMouseLeave={() => {
        if (preview.url) setPreview({ url: null, x: 0, y: 0 });
      }}
    >
      <Card
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Button icon={<LeftOutlined />} type="text" aria-label="Back" onClick={() => navigate(-1)} />
            <span style={{ fontSize: 24, fontWeight: 600 }}>DCC Local Files</span>
          </span>
        }
        style={{ margin: 24 }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text
              type={
                error
                  ? 'danger'
                  : !directoryHandle || isLoading
                    ? 'secondary'
                    : rows.length === 0
                      ? 'danger'
                      : 'secondary'
              }
            >
              {!directoryHandle
                ? 'Select a local folder to list files.'
                : error
                  ? 'Paused due to an error while reading files. Choose Continue or Stop in the dialog.'
                  : isLoading
                    ? 'Reading files from local folder...'
                    : rows.length === 0
                      ? 'Selected folder is empty (no files found).'
                      : `${filteredRows.length} files in current batch view (files ${rows.length === 0 ? 0 : batchOffset + 1}-${Math.min(batchOffset + (batchSize > 0 ? batchSize : 500), rows.length)} of ${rows.length} loaded)`}
            </Text>
            <Space size={8}>
              <span>
                Thumbnail size:{' '}
                <InputNumber
                  size="small"
                  min={80}
                  max={400}
                  step={20}
                  value={thumbSize}
                  onChange={value => {
                    const num = typeof value === 'number' && isFinite(value) && value > 0 ? value : 200;
                    setThumbSize(num);
                  }}
                  style={{ width: 60, marginLeft: 2 }}
                />
              </span>
              <span>
                Batch size:
                {' '}
                <InputNumber
                  size="small"
                  min={50}
                  max={100000}
                  step={50}
                  value={batchSize}
                  onChange={value => {
                    const num = typeof value === 'number' && isFinite(value) && value > 0 ? value : 500;
                    setBatchSize(num);
                    setBatchOffset(0);
                    setTableCurrentPage(1);
                  }}
                  style={{ width: 80, marginLeft: 2 }}
                />
              </span>
              <Button
                size="small"
                onClick={handleRefresh}
                disabled={!directoryHandle || isLoading}
              >
                Refresh list
              </Button>
              <Button
                size="small"
                type="primary"
                disabled={!directoryHandle || !selectedRowIds.length}
                onClick={() => setIsCopyModalOpen(true)}
              >
                Copy to Local
              </Button>
              <Button
                size="small"
                onClick={goPrevBatch}
                disabled={!directoryHandle || isLoading || batchOffset === 0}
              >
                Previous batch
              </Button>
              <Button
                size="small"
                onClick={goNextBatch}
                disabled={!directoryHandle || isLoading || !hasMoreBatches}
              >
                Next batch
              </Button>
              {isFileSystemApiAvailable && (
                <Button size="small" onClick={selectLocalFolder}>
                  Select local folder
                </Button>
              )}
            </Space>
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'nowrap' }}>
            <Input
              placeholder="file_name"
              allowClear
              value={fileNameFilter}
              onChange={e => {
                setFileNameFilter(e.target.value);
                setTableCurrentPage(1);
              }}
              style={{ width: 220 }}
            />

            <Input
              placeholder="path (contains)"
              allowClear
              value={pathFilter}
              onChange={e => {
                setPathFilter(e.target.value);
                setBatchOffset(0);
                setTableCurrentPage(1);
              }}
              style={{ width: 360 }}
            />

            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder="type"
              value={typeFilter}
              onChange={vals => {
                setTypeFilter((vals || []) as string[]);
                setBatchOffset(0);
                setTableCurrentPage(1);
              }}
              style={{ width: 200 }}
              optionFilterProp="label"
              options={typeOptions.map(t => ({ value: t, label: t }))}
            />

            <RangePicker
              allowEmpty={[true, true]}
              placeholder={['updated from', 'updated to']}
              value={dateModifiedRange as any}
              onChange={v => {
                setDateModifiedRange((v as any) || null);
                setBatchOffset(0);
                setTableCurrentPage(1);
              }}
              style={{ width: 220 }}
            />
            <Button
              size="small"
              onClick={resetFilters}
              disabled={isLoading && !directoryHandle}
            >
              Reset filters
            </Button>
          </div>

          <StandardTable
            columns={columns as any}
            dataSource={currentBatchRows}
            rowKey={(record: any) => String(record?.id ?? '')}
            loading={isLoading}
            rowSelection={{
              selectedRowKeys: selectedRowIds,
              onChange: keys => setSelectedRowIds((keys || []).map(k => String(k)) as string[]),
            }}
            onRow={(record: any) => ({
              onClick: (event: React.MouseEvent) => {
                const id = String((record as LocalFileRow).id);
                setActiveRowId(id);
                handleRowPreview(event, record as LocalFileRow);
              },
              onDoubleClick: () => handleRowOpenInNewTab(record as LocalFileRow),
            })}
            pagination={{
              current: tableCurrentPage,
              pageSize: tablePageSize,
              total: filteredRows.length,
              showSizeChanger: true,
              pageSizeOptions: ['20', '50', '100', '200', '500'],
              showTotal: (_total: number, range: [number, number]) =>
                `${range[0]}-${range[1]} of ${filteredRows.length}`,
            }}
            onChange={handleTableChange}
            size="small"
            className="compact-table zebra-table left-align-table"
            rowClassName={(record: any, index: number) => {
              const base = index % 2 === 0 ? 'zebra-row' : '';
              const isActive = activeRowId != null && String(record?.id) === activeRowId;
              if (isActive) {
                return base ? `${base} active-row` : 'active-row';
              }
              return base;
            }}
            style={{ padding: '2px' }}
            scroll={{ x: 'max-content' }}
          />
        </Space>
      </Card>

      <Modal
        open={isCopyModalOpen}
        title="Copy selected files to local folder"
        onCancel={() => setIsCopyModalOpen(false)}
        onOk={async () => {
          await copySelectedToDestination();
          setIsCopyModalOpen(false);
        }}
        okButtonProps={{ disabled: !destinationHandle || !selectedRowIds.length, loading: isCopying }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            Selected files: {selectedRowIds.length}
          </Text>
          <Button onClick={selectDestinationFolder}>Select destination folder</Button>
          <Text type={destinationHandle ? 'secondary' : 'danger'}>
            {destinationHandle
              ? 'Destination folder selected. Confirm to copy the selected files.'
              : 'No destination folder selected yet.'}
          </Text>
          {currentCopyName && (
            <Text type="secondary">Currently copying: {currentCopyName}</Text>
          )}
        </Space>
      </Modal>

      {preview.url && (
        <div
          style={{
            position: 'fixed',
            top: preview.y,
            left: preview.x,
            pointerEvents: 'none',
            zIndex: 1000,
            border: '2px solid #ccc',
            backgroundColor: '#fff',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
          }}
        >
          <img src={preview.url} alt="Preview" style={{ width: `${thumbSize}px`, display: 'block' }} />
        </div>
      )}
    </div>
  );
};

export default DccLocalFiles;
